import { useContext } from 'react';
import isArray from 'lodash/isArray';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useDeviationAttributes } from '../../../../DeviationViews/hooks/useDeviationAttributes';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { DeviationAttributes } from '../../../types';
import { WindowBreakpoint, ElementGroup } from '../../types';
import {
  StandardDeviationElementPlacement,
  StandardDeviationElementPlacementMap,
  NOT_INCLUDED_VISIBILITY_GROUP,
} from './types';
import {
  getDeviationType,
  isImageDeviation,
  isSimpleDeviation,
} from '../../../../DeviationViews/_helpers';
import { DeviationTypes } from '../../../../types/deviation';
import { getAdoptableBuyerElementGroupBase } from '../../sharedBases/adoptableBuyer';
import { getAdoptableSellerElementGroupBase } from '../../sharedBases/adoptableSeller';
import { useWindowBreakpoint } from '../../utils';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';

/**
 * A collection of values that help disparate elements decide their placement.
 */
interface DeterminationContext {
  deviation: PapiDeviation;
  deviationAttributes: DeviationAttributes;
  isLoggedIn: boolean;
  isMobile: boolean;
  isAuthor: boolean;
  windowBreakpoint: WindowBreakpoint;
  elementGroups: StandardDeviationElementPlacementMap['elementGroups'];
  flags: string[];
}

/**
 * A set of common checks against the adoptable element groups that other controls use to collapse.
 */
function shouldCollapseForAdoptableElementGroups(
  determinationContext: DeterminationContext
) {
  const {
    elementGroups: { adoptableBuyer, adoptableSeller },
    windowBreakpoint,
  } = determinationContext;
  return (
    // If the adoptable buyer group has a couple elements, there isn't enough room. Signal that we should collapse this control.
    (adoptableBuyer.groupPlacement ===
      StandardDeviationElementPlacement.PrimaryBar &&
      adoptableBuyer.countOnBar >= 2) ||
    // On smaller desktop screens, we'll signal to collapse this control if either adoptable-related element groups are active. Regardless
    // of the number of elements visible from each.
    ((adoptableBuyer.groupPlacement ===
      StandardDeviationElementPlacement.PrimaryBar ||
      adoptableSeller.groupPlacement ===
        StandardDeviationElementPlacement.PrimaryBar) &&
      windowBreakpoint === WindowBreakpoint.DesktopSmallSize)
  );
}

function getEditBackgroundPlacement(
  determinationContext: DeterminationContext
) {
  return determinationContext.deviationAttributes.isBackgroundEditable
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

/**
 * Will return a boolean indicating whether the provided menu {@link targetPlacement} is included in the placement map.
 *
 * This is useful for determining if we should include the more menu buttons at all. Prevents downstream consumers from
 * having to do it themselves.
 */
function hasElementInTargetMoreMenu(
  elementPlacementMap: StandardDeviationElementPlacementMap,
  targetPlacement:
    | StandardDeviationElementPlacement.PrimaryBarMenu
    | StandardDeviationElementPlacement.SecondaryBarMenu
): boolean {
  const elementPlacementValues = Object.values(elementPlacementMap);

  for (let i = 0; i < elementPlacementValues.length; i++) {
    const currentPlacementValue = elementPlacementValues[i];
    if (
      isArray(currentPlacementValue) &&
      currentPlacementValue.includes(targetPlacement)
    ) {
      return true;
    } else if (currentPlacementValue === targetPlacement) {
      return true;
    }
  }

  return false;
}

/**
 * Returns an object that maps ActionBar elements to their {@link StandardDeviationElementPlacement}.
 * (e.g. is this element on the bar? collapsed into the more menu? completely absent?)
 */
export function useStandardDeviationElementPlacementMap(
  deviation: PapiDeviation
): StandardDeviationElementPlacementMap {
  const flags = useEnabledFlagList();
  const windowBreakpoint = useWindowBreakpoint();
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const isOwner = !!deviation.extended?.adoptable?.isOwner;
  const deviationAttributes = useDeviationAttributes(deviation, isAuthor);

  // These are collections of buttons. They can take up large amounts of space on the action bar,
  // so we use this object to determine if other more general controls should be removed/collapsed.
  const elementGroups: StandardDeviationElementPlacementMap['elementGroups'] = {
    adoptableBuyer: buildElementGroup(
      getAdoptableBuyerElementGroupBase(deviation, isMobile, isOwner),
      isMobile
    ),
    adoptableSeller: buildElementGroup(
      getAdoptableSellerElementGroupBase(deviation, isMobile, isOwner),
      isMobile
    ),
  };

  const determinationContext: DeterminationContext = {
    deviation,
    isAuthor,
    isMobile,
    isLoggedIn,
    deviationAttributes,
    windowBreakpoint,
    elementGroups,
    flags,
  };

  const intermediaryMapping: StandardDeviationElementPlacementMap = {
    comment: getCommentPlacement(determinationContext),
    premiumDownload: getPremiumDownloadPlacement(determinationContext),
    freeDownload: getFreeDownloadPlacement(determinationContext),
    addToGroup: getAddToGroupPlacement(determinationContext),
    awardBadge: getAwardBadgePlacement(determinationContext),
    favourite: getFavouritePlacement(determinationContext),
    privateCollection: getPrivateCollectionPlacement(determinationContext),
    boostDeviation: getBoostDeviationPlacement(determinationContext),
    sellDeviation: getSellDeviationPlacement(determinationContext),
    subscribe: useSubscribePlacement(determinationContext),
    editBackground: getEditBackgroundPlacement(determinationContext),
    deleteDeviation: getDeleteDeviationPlacement(determinationContext),
    editDeviation: getEditDeviationPlacement(determinationContext),
    commissionStatus: getCommissionStatusPlacement(determinationContext),
    orderCommission: getOrderCommissionPlacement(determinationContext),
    share: getSharePlacement(determinationContext),
    report: getReportPlacement(determinationContext),
    fullscreen: getFullscreenPlacement(determinationContext),
    elementGroups: elementGroups,
    viewOrderHistory: getViewOrderHistoryPlacement(determinationContext),
    unpublish: getUnpublishPlacement(determinationContext),
    draw: getDrawPlacement(determinationContext),
    meta: {
      hasPrimaryMoreActionsMenu: false,
      hasSecondaryMoreActionsMenu: false,
    },
  };

  intermediaryMapping.meta = {
    // ElementPlacementMap.elementGroups has no controls that appear in menus for the time being, so we're
    // only testing the root values of elementPlacementMap.
    hasPrimaryMoreActionsMenu: hasElementInTargetMoreMenu(
      intermediaryMapping as StandardDeviationElementPlacementMap,
      StandardDeviationElementPlacement.PrimaryBarMenu
    ),

    // ElementPlacementMap.elementGroups has no controls that appear in menus for the time being, so we're
    // only testing the root values of ElementPlacementMap.
    hasSecondaryMoreActionsMenu: hasElementInTargetMoreMenu(
      intermediaryMapping as StandardDeviationElementPlacementMap,
      StandardDeviationElementPlacement.SecondaryBarMenu
    ),
  };

  return intermediaryMapping;
}

function getDeleteDeviationPlacement(
  determinationContext: DeterminationContext
) {
  return determinationContext.deviationAttributes.isDeletable
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

function getUnpublishPlacement(determinationContext: DeterminationContext) {
  const { deviation, isAuthor } = determinationContext;
  const isTier = getDeviationType(deviation) === DeviationTypes.TIER;
  const isSimple = isSimpleDeviation(deviation);
  if (!isSimple && !isTier && isAuthor) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.NotIncluded;
}

function getDrawPlacement(determinationContext: DeterminationContext) {
  const { deviation, isAuthor } = determinationContext;

  const isImage = isImageDeviation(deviation);
  const isImageForRealz = !deviation.isDefaultImage;
  const isSimple = isSimpleDeviation(deviation);
  if (isAuthor && isImage && isImageForRealz && !isSimple) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.NotIncluded;
}

function getEditDeviationPlacement(determinationContext: DeterminationContext) {
  // For now, the tier edit popup doesn't work on mobile
  if (
    determinationContext.isMobile &&
    getDeviationType(determinationContext.deviation) === DeviationTypes.TIER
  ) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  return determinationContext.deviationAttributes.isEditable
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

function getCommissionStatusPlacement(
  determinationContext: DeterminationContext
) {
  const deviationType = getDeviationType(determinationContext.deviation);
  if (
    deviationType === DeviationTypes.COMMISSION &&
    determinationContext.isMobile &&
    determinationContext.deviationAttributes.isEditable
  ) {
    return StandardDeviationElementPlacement.PrimaryBar;
  }

  return StandardDeviationElementPlacement.NotIncluded;
}

function getOrderCommissionPlacement(
  determinationContext: DeterminationContext
) {
  const deviationType = getDeviationType(determinationContext.deviation);
  if (
    deviationType === DeviationTypes.COMMISSION &&
    determinationContext.isMobile &&
    !determinationContext.isAuthor &&
    determinationContext.deviationAttributes.isAuthorCommissionable
  ) {
    return StandardDeviationElementPlacement.PrimaryBar;
  }

  return StandardDeviationElementPlacement.NotIncluded;
}

function getViewOrderHistoryPlacement(
  determinationContext: DeterminationContext
) {
  const { deviationAttributes, isAuthor } = determinationContext;

  // Only show the "View order history" link to owner
  if (deviationAttributes.isPurchasable && isAuthor) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.NotIncluded;
}

function getCommentPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['comment'] {
  const {
    deviation,
    deviationAttributes: { isCommentable },
    elementGroups,
  } = determinationContext;
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;

  if (!isCommentable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (
    shouldCollapseForAdoptableElementGroups(determinationContext) ||
    // In addition to the common checks provided by shouldCollapseForAdoptableElementGroups,
    // we want to collapse this control in all cases the adoptableSeller group is on the primary bar.
    elementGroups.adoptableSeller.groupPlacement ===
      StandardDeviationElementPlacement.PrimaryBar
  ) {
    return isJournal
      ? [
          StandardDeviationElementPlacement.PrimaryBarMenu,
          StandardDeviationElementPlacement.SecondaryBarMenu,
        ]
      : [StandardDeviationElementPlacement.PrimaryBarMenu];
  }

  return isJournal
    ? [
        StandardDeviationElementPlacement.PrimaryBar,
        StandardDeviationElementPlacement.SecondaryBar,
      ]
    : [StandardDeviationElementPlacement.PrimaryBar];
}

function getFreeDownloadPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['freeDownload'] {
  const {
    isLoggedIn,
    isMobile,
    deviationAttributes: { isDownloadable, isAdoptable },
  } = determinationContext;

  if (!isDownloadable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  if (isMobile && !isLoggedIn) {
    return isAdoptable
      ? StandardDeviationElementPlacement.NotIncluded
      : StandardDeviationElementPlacement.SecondaryBar;
  }

  return StandardDeviationElementPlacement.PrimaryBar;
}

function getPremiumDownloadPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['premiumDownload'] {
  const {
    deviationAttributes: { isPurchasable },
  } = determinationContext;

  if (!isPurchasable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.PrimaryBar;
}

function getFavouritePlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['favourite'] {
  const { deviation, deviationAttributes } = determinationContext;
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;

  if (!deviationAttributes.isFavouritable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  return isJournal
    ? [
        StandardDeviationElementPlacement.PrimaryBar,
        StandardDeviationElementPlacement.SecondaryBar,
      ]
    : [StandardDeviationElementPlacement.PrimaryBar];
}

function getAddToGroupPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['addToGroup'] {
  const {
    deviation,
    deviationAttributes: { isGroupSubmittable },
    isAuthor,
  } = determinationContext;
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;

  if (!isGroupSubmittable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (isAuthor) {
    return isJournal
      ? [
          StandardDeviationElementPlacement.PrimaryBar,
          StandardDeviationElementPlacement.SecondaryBar,
        ]
      : [StandardDeviationElementPlacement.PrimaryBar];
  }

  return isJournal
    ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu,
      ]
    : [StandardDeviationElementPlacement.PrimaryBarMenu];
}

function getAwardBadgePlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['awardBadge'] {
  const {
    deviationAttributes: { isCoreBadgeAwardable },
  } = determinationContext;

  if (!isCoreBadgeAwardable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.PrimaryBar;
}

function getPrivateCollectionPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['privateCollection'] {
  const { isAuthor } = determinationContext;

  if (isAuthor) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
    return StandardDeviationElementPlacement.PrimaryBarMenu;
  }

  return StandardDeviationElementPlacement.PrimaryBar;
}

function getFullscreenPlacement({
  isLoggedIn,
}: DeterminationContext): StandardDeviationElementPlacementMap['fullscreen'] {
  return isLoggedIn
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

function getBoostDeviationPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['boostDeviation'] {
  const { deviation } = determinationContext;

  return deviation.extended?.isBoostable
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

function getSellDeviationPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['sellDeviation'] {
  const { deviationAttributes, isMobile } = determinationContext;

  if (!deviationAttributes.isSellable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  return isMobile
    ? StandardDeviationElementPlacement.SecondaryBar
    : StandardDeviationElementPlacement.PrimaryBar;
}

function useSubscribePlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['subscribe'] {
  const tier = useSubscriptionTier(determinationContext.deviation);
  return !determinationContext.deviationAttributes.hasFlag && !!tier
    ? StandardDeviationElementPlacement.PrimaryBar
    : StandardDeviationElementPlacement.NotIncluded;
}

function getSharePlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['share'] {
  const { deviation, deviationAttributes } = determinationContext;
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;

  if (!deviationAttributes.isShareable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  return isJournal
    ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu,
      ]
    : [StandardDeviationElementPlacement.PrimaryBarMenu];
}

function getReportPlacement(
  determinationContext: DeterminationContext
): StandardDeviationElementPlacementMap['report'] {
  const { deviation, deviationAttributes } = determinationContext;
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;

  if (!deviationAttributes.isReportable) {
    return StandardDeviationElementPlacement.NotIncluded;
  }

  return isJournal
    ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu,
      ]
    : [StandardDeviationElementPlacement.PrimaryBarMenu];
}

function buildElementGroup<
  T extends ElementGroup<StandardDeviationElementPlacement>,
>(
  groupValues: Omit<T, 'countOnBar' | 'groupPlacement'> | false,
  isMobile: boolean
): T {
  if (!groupValues) {
    return NOT_INCLUDED_VISIBILITY_GROUP as T;
  }

  const countOnBar = Object.values(groupValues).filter(
    isVisible => isVisible
  ).length;

  let groupPlacement = StandardDeviationElementPlacement.NotIncluded;
  if (countOnBar > 0) {
    groupPlacement = isMobile
      ? StandardDeviationElementPlacement.SecondaryBar
      : StandardDeviationElementPlacement.PrimaryBar;
  }

  return {
    groupPlacement,
    countOnBar,
    ...groupValues,
  } as T;
}
